import React, { useState, useEffect, useMemo } from 'react';
import ReactDataGrid from '@inovua/reactdatagrid-community';
import { filterTypes } from './helper';
import { useGridFilters } from './useGridFilters';

const gridStyle = {
  minHeight: 'calc(100vh - 180px)',
  marginTop: 0,
  boxShadow: '0px 3px 10px 0 rgba(0, 0, 0, 0.08)',
  overflow: 'hidden'
};

export const GridWrapper = ({
  columns,
  setLoading,
  totalRecords,
  dataSource,
  loadingMessage,
  renderColumnContextMenu,
  selectedData,
  handleSelect,
  gridClass,
  vissibleColumns,
  refetch = false,
  customGridStyle = {},
  rowHeight = 40,
  recordsLimit = 50,
  enableSelection = true,
  activateRowOnFocus = true,
  showActiveRowIndicator = false,
  checkboxOnlyRowSelect = false,
  checkboxColumn = false,
  staticColumns = ['actions'],
  storageKey = null,
  strippedRows = false,
  showCellBorders = 'horizontal',
  bodyScroll = true,
  idProperty,
  pagination = true,
  clientSide = false
}) => {
  const [limit, setLimit] = useState(recordsLimit);
  const [dynamicColumns, setDynamicColumns] = useState(columns);
  const [gridRef, setGridRef] = useState(null);
  const {
    filterValue,
    sortInfo,
    defaultFilterValue,
    defaultSortInfo,
    onFilterValueChange,
    onSortInfoChange
  } = useGridFilters(storageKey, columns);

  // Apply filters and sorting to dataSource (client-side)
  const transformedData = useMemo(() => {
    if (!clientSide || !Array.isArray(dataSource)) return dataSource;

    let data = [...dataSource];

    // ✅ Apply Filters
    if (filterValue?.length) {
      data = data.filter(row => {
        return filterValue.every(
          ({ name, value, type, operator = 'contains' }) => {
            const rowValRaw = row[name];

            if (value == null || value === '') return true;

            // Handle date range (operator always eq)
            if (
              type === 'date' &&
              operator === 'eq' &&
              typeof value === 'object'
            ) {
              const rowDate = rowValRaw ? new Date(rowValRaw) : null;
              const startDate = value.startDate
                ? new Date(value.startDate)
                : null;
              const endDate = value.endDate ? new Date(value.endDate) : null;

              if (!rowDate || isNaN(rowDate)) return false;

              const onlyDate = d => new Date(d.toDateString());
              const rowOnly = onlyDate(rowDate);

              if (startDate && isNaN(startDate)) return true;
              if (endDate && isNaN(endDate)) return true;

              if (startDate && endDate) {
                return (
                  rowOnly >= onlyDate(startDate) && rowOnly <= onlyDate(endDate)
                );
              } else if (startDate) {
                return rowOnly >= onlyDate(startDate);
              } else if (endDate) {
                return rowOnly <= onlyDate(endDate);
              }

              return true; // if both start & end empty
            }

            // Handle string or select values
            const rowVal =
              rowValRaw != null ? rowValRaw.toString().toLowerCase() : '';
            const filterVal =
              value != null
                ? value
                    .toString()
                    .replace(/\$|\s+/g, '')
                    .toLowerCase()
                : '';

            if (operator === 'contains') return rowVal.includes(filterVal);
            if (operator === 'eq') return rowVal === filterVal;

            return true; // fallback
          }
        );
      });
    }

    // ✅ Apply Sorting
    if (sortInfo?.name) {
      const { name, dir } = sortInfo;
      data.sort((a, b) => {
        const aVal = a[name];
        const bVal = b[name];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (dir === 1) return aVal > bVal ? 1 : -1;
        if (dir === -1) return aVal < bVal ? 1 : -1;
        return 0;
      });
    }

    return data;
  }, [clientSide, dataSource, filterValue, sortInfo]);

  // ✅ Get visible columns
  const getVissibleColumns = () => {
    return columns.map(dc => ({
      ...dc,
      visible: staticColumns.includes(dc.name)
        ? true
        : !!vissibleColumns[dc.name]
    }));
  };

  useEffect(() => {
    if (vissibleColumns && Object.keys(vissibleColumns).length) {
      setDynamicColumns(getVissibleColumns());
    }
  }, [vissibleColumns]);

  // ✅ Scroll to top on page change
  const onSkipChange = () => {
    if (gridRef && gridRef.current) {
      setTimeout(() => {
        gridRef.current.setScrollTop(0);
      }, 50);
    }
  };

  useEffect(() => {
    if (bodyScroll) removeScroll('overflow:hidden');
    return () => removeScroll('');
  }, []);

  const removeScroll = styles => {
    document.getElementsByTagName('body')[0].style.cssText = styles;
  };

  const onReload = () => {
    if (gridRef && gridRef.current) {
      gridRef.current.reload();
    }
  };

  useEffect(() => {
    if (refetch) {
      onReload();
    }
  }, [refetch]);

  return (
    <ReactDataGrid
      checkboxColumn={checkboxColumn}
      checkboxOnlyRowSelect={checkboxOnlyRowSelect}
      onReady={setGridRef}
      idProperty={idProperty || 'id'}
      style={{ ...gridStyle, ...customGridStyle }}
      className={gridClass || ''}
      columns={dynamicColumns}
      limit={limit}
      onLimitChange={setLimit}
      pagination={pagination}
      rowHeight={rowHeight}
      dataSource={transformedData} //Inject filtered/sorted data if clientSide is true
      onLoadingChange={setLoading}
      onSortInfoChange={onSortInfoChange}
      sortInfo={sortInfo || defaultSortInfo}
      defaultSortInfo={defaultSortInfo}
      onFilterValueChange={onFilterValueChange}
      filterValue={filterValue || defaultFilterValue}
      defaultFilterValue={defaultFilterValue}
      filterTypes={filterTypes}
      showHoverRows={true}
      showCellBorders={showCellBorders}
      showColumnMenuTool={true}
      loadingText={loadingMessage}
      renderColumnContextMenu={renderColumnContextMenu}
      selected={selectedData}
      onSelectionChange={handleSelect}
      enableSelection={enableSelection}
      showZebraRows={strippedRows}
      activateRowOnFocus={activateRowOnFocus}
      showActiveRowIndicator={showActiveRowIndicator}
      onSkipChange={onSkipChange}
    />
  );
};
